import React, { useEffect, useMemo, useState } from 'react';
import { Button, SparkIcon, TagGeneral, Text, TooltipTrigger, sparkToast } from '@spark';
import ClassicMetricsConfigModal from '@/features/evaluation/components/Summarization/ClassicMetricsConfigModal';
import {
    CLASSIC_METRICS_DEFAULTS,
    getMetricsInfoForScope,
} from '@/features/evaluation/components/Summarization/classicMetricsDefaults';
import { updateLlmJudgeConfig } from '@/features/evaluation/api/summarizationApi';

const SCOPE_COPY = {
    classic: {
        title: 'Classic metrics criteria',
        tooltip:
            'Pre-configure which classic metrics (ROUGE, BLEU, METEOR, BERTScore, ...) are shown on the Evaluations page. All metrics are still computed; this only controls what is displayed.',
    },
    accuracy: {
        title: 'Accuracy metrics criteria',
        tooltip:
            'Pre-configure which accuracy metrics (Exact Match, Fuzzy Similarity (MinHash), Dict Match) are shown on the Evaluations page. All metrics are still computed; this only controls what is displayed.',
    },
};

/**
 * Pre-configure which metrics of one group are shown on the Evaluations page,
 * from the Train page (generative tasks only). `scope` selects the group:
 * 'classic' (ROUGE, BLEU, ...) or 'accuracy' (Exact Match, Fuzzy Similarity
 * (MinHash), Dict Match — content generation tasks only). Reuses the
 * evaluation page's metrics config modal and persists the shared
 * `classic_metrics_config` usecase field, so the selection is already in
 * place when evaluation runs.
 */
const MetricsCriteriaSelector = ({ usecase, onPersisted, scope = 'classic' }) => {
    const copy = SCOPE_COPY[scope] || SCOPE_COPY.classic;
    const [modalOpen, setModalOpen] = useState(false);
    const [classicConfig, setClassicConfig] = useState(() => ({
        ...CLASSIC_METRICS_DEFAULTS,
        ...(usecase?.classic_metrics_config || {}),
    }));

    useEffect(() => {
        setClassicConfig({
            ...CLASSIC_METRICS_DEFAULTS,
            ...(usecase?.classic_metrics_config || {}),
        });
    }, [usecase]);

    const enabledTitles = useMemo(
        () =>
            getMetricsInfoForScope(scope)
                .filter((m) => classicConfig?.[m.switchKey] !== false)
                .map((m) => m.title),
        [classicConfig, scope],
    );

    const persist = async (next) => {
        setClassicConfig(next);
        try {
            await updateLlmJudgeConfig({
                client_id: usecase?.client_id,
                usecase_id: usecase?._id,
                classic_metrics_config: next,
            });
            onPersisted?.(next);
        } catch (error) {
            console.error('Failed to save metrics configuration:', error);
            sparkToast.error('Failed to save metrics configuration');
        }
    };

    return (
        <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <Text variant="base-semibold">{copy.title}</Text>
                    <TooltipTrigger text={copy.tooltip}>
                        <SparkIcon slug="circle-info" size="small" />
                    </TooltipTrigger>
                </div>
                <Button
                    variant="secondary"
                    size="small"
                    startIcon={<SparkIcon slug="adjust" size="small" />}
                    disableWhenReadOnly
                    onClick={() => setModalOpen(true)}
                >
                    Configure
                </Button>
            </div>
            <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {enabledTitles.length > 0 ? (
                    enabledTitles.map((title) => <TagGeneral key={title} label={title} />)
                ) : (
                    <Text variant="base-regular" color="secondary">
                        No metrics enabled
                    </Text>
                )}
            </div>

            <ClassicMetricsConfigModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                value={classicConfig}
                onChange={persist}
                scope={scope}
            />
        </div>
    );
};

export default MetricsCriteriaSelector;
